
import Component from "vue-class-component"
import PageHeader from "@/superannuation/PageHeader.vue"
import HelpIcon from "@/ui/HelpIcon.vue"
import Vue from "vue"
import {Company, Member, Plan} from "@/superannuation/PlanService"
import SelectDropDown from "@/components/SelectDropDown.vue"
import {restDownloadFile, restPostDownloadFile} from "@/utils/rest"
import Axios, {AxiosError, AxiosResponse} from "axios"
import {theme} from "@/theme"
import ErrorMessage from "@/components/ErrorMessage.vue"
import {format} from "date-fns"
import {Inject} from "vue-property-decorator"
import {AuthenticationService} from "@/AuthenticationService"
import {QPopupProxy} from "quasar"
import {formatMoney, getIsoDate} from "@/utils/formatter"
import _ from "lodash"

class SelectedMember {
  memberName: string | null
  companyId: string | null
  memberCategory: MemberCategory | null
  salary: number
  dateOfBirth: string | null
  occupationGroup: string | null
  gender: string | null
  state: string | null
  currentInsurance: CurrentInsurance | null
}

interface CurrentInsurance {
  deathCover: number
  tpdCover: number
  sciCover: number
  deathPremium: number
  tpdPremium: number
  sciPremium: number
}

interface MemberCategory {
  id: string
  name: string
  active: boolean
}

class InsuranceCalculationRequest {
  calculationDate: string | null
  memberId: string | null
  voluntaryDeathCover = 0
  voluntaryTpdCover = 0
  voluntarySciCover = 0
  selectedMember = new SelectedMember()

  isPositive(): boolean {
    return this.voluntaryDeathCover > 0 || this.voluntaryTpdCover > 0 || this.voluntarySciCover > 0
  }
}

interface InsuranceCalculation {
  memberName: string
  planName: string
  default: Array<Insurance>
  voluntary: Array<Insurance>
  sci: Array<SalaryContinuanceInsurance>
  deathTpdAal: number
  sciAal: number
  defaultSciBenefitPeriod: string
  defaultSciDaysWait: string
  defaultPremiumTotal: number
  voluntaryPremiumTotal: number
  defaultMonthlyPremiumTotal: number
  voluntaryMonthlyPremiumTotal: number
  premiumTotal: number
  monthlyPremiumTotal: number
}

interface Insurance {
  type: string
  cover: number
  rate: number | null
  annualPremium: number | null
  monthlyPremium: number | null
}

interface SalaryContinuanceInsurance {
  periodWait: string
  rate: number
  annualPremium: number
  monthlyPremium: number
}

@Component({
  components: {
    ErrorMessage,
    SelectDropDown,
    PageHeader,
    HelpIcon,
  },
})
export default class InsuranceCalculationView extends Vue {
  @Inject()
  authenticationService: AuthenticationService

  t = theme()

  readonly memberOptionExisting = "EXISTING_MEMBER"
  readonly memberOptionNew = "NEW_MEMBER"
  readonly memberOptions = [
    {label: "Existing", value: this.memberOptionExisting},
    {label: "New Member", value: this.memberOptionNew},
  ]

  readonly genders = [
    {label: "Male", value: "Male"},
    {label: "Female", value: "Female"},
  ]

  $refs: {
    companySelect: SelectDropDown
    memberSelect: SelectDropDown
    groupSelect: SelectDropDown
    memberCategorySelect: SelectDropDown
    stateSelect: SelectDropDown
    qCalculationDateProxy: QPopupProxy
    qBirthDateProxy: QPopupProxy
  }

  memberOption = this.memberOptionExisting

  companyId: string | null = null
  member: Member | null = null
  plan: Plan | null = null

  calculationDate: string | null = format(Date.now(), "dd/MM/yyyy")
  dateOfBirth: string | null = null

  newMemberName: string | null = null

  request = new InsuranceCalculationRequest()

  message: string | null = null

  insuranceCalculationInProgress = false

  insuranceCalculationData: InsuranceCalculation | null = null

  isAdmin = false

  dateRule = (v: string) =>
    !this.isNewMember || /^([0-2]\d|(3)[01])(\/)(((0)\d)|((1)[0-2]))(\/)\d{4}$/.test(v)

  async created() {
    this.isAdmin = await this.authenticationService.isAdmin()
  }

  private isValidNewMemberRequest() {
    return (
      this.request.selectedMember.memberName == null ||
      this.request.selectedMember.companyId == null ||
      this.request.selectedMember.salary <= 0 ||
      this.dateOfBirth == null ||
      this.request.selectedMember.memberCategory == null ||
      this.request.selectedMember.gender == null ||
      this.request.selectedMember.occupationGroup == null ||
      this.request.selectedMember.state == null
    )
  }

  get isInsuranceCalculationDisabled(): boolean {
    return (
      this.calculationDate == null ||
      (this.isNewMember && this.isValidNewMemberRequest()) ||
      (!this.isNewMember && (this.member == null || this.request.selectedMember.salary <= 0))
    )
  }

  get selectedCompanyPlanPlatform(): string | null {
    return this.plan == null ? null : `${this.plan.name} / ${this.plan.platform.name}`
  }

  get isNewMember(): boolean {
    return this.memberOption === this.memberOptionNew
  }

  private getInsurance(insurance: Insurance): Record<string, string> {
    return {
      type: insurance.type,
      cover: formatMoney(insurance.cover),
      rate: insurance.rate == null ? "" : insurance.rate.toFixed(2),
      annualPremium: insurance.annualPremium == null ? "" : formatMoney(insurance.annualPremium),
      monthlyPremium: insurance.monthlyPremium == null ? "" : formatMoney(insurance.monthlyPremium),
    }
  }

  private addHeader(data: Array<Record<string, unknown>>, heading: string) {
    data.push({
      type: heading,
      cover: "Cover",
      rate: "Rate",
      annualPremium: "Premium",
      monthlyPremium: "Monthly",
    })
  }

  get insuranceDataRows(): Array<Record<string, unknown>> | null {
    const current = this.request.selectedMember.currentInsurance
    return current
      ? [
          {
            type: "Current",
            cover: "Cover",
            annualPremium: "Premium",
            monthlyPremium: "Monthly",
          },
          {
            type: "Death",
            cover: formatMoney(current.deathCover),
            annualPremium: formatMoney(current.deathPremium * 12),
            monthlyPremium: formatMoney(current.deathPremium),
          },
          {
            type: "TPD",
            cover: formatMoney(current.tpdCover),
            annualPremium: formatMoney(current.tpdPremium * 12),
            monthlyPremium: formatMoney(current.tpdPremium),
          },
          {
            type: "SCI",
            cover: formatMoney(current.sciCover),
            annualPremium: formatMoney(current.sciPremium * 12),
            monthlyPremium: formatMoney(current.sciPremium),
          },
        ]
      : null
  }

  get insuranceCalculationDataRows(): Array<Record<string, unknown>> | null {
    const insuranceCalculationDataRows: Array<Record<string, unknown>> = new Array<
      Record<string, unknown>
    >()

    if (this.insuranceCalculationData) {
      this.addHeader(insuranceCalculationDataRows, "Default")
      insuranceCalculationDataRows.push(
        ...this.insuranceCalculationData.default.map(this.getInsurance)
      )

      insuranceCalculationDataRows.push({
        type: "",
        cover: "",
        rate: "",
        annualPremium: formatMoney(this.insuranceCalculationData.defaultPremiumTotal),
        monthlyPremium: formatMoney(this.insuranceCalculationData.defaultMonthlyPremiumTotal),
      })

      if (this.request.isPositive()) {
        insuranceCalculationDataRows.push({
          type: "Voluntary",
          cover: "",
          rate: "",
          annualPremium: "",
          monthlyPremium: "",
        })

        insuranceCalculationDataRows.push(
          ...this.insuranceCalculationData.voluntary.map(this.getInsurance)
        )

        insuranceCalculationDataRows.push({
          type: "",
          cover: "",
          rate: "",
          annualPremium: formatMoney(this.insuranceCalculationData.voluntaryPremiumTotal),
          monthlyPremium: formatMoney(this.insuranceCalculationData.voluntaryMonthlyPremiumTotal),
        })
      }

      insuranceCalculationDataRows.push({
        type: "Total",
        cover: "",
        rate: "",
        annualPremium: formatMoney(this.insuranceCalculationData.premiumTotal),
        monthlyPremium: formatMoney(this.insuranceCalculationData.monthlyPremiumTotal),
      })
    }

    return insuranceCalculationDataRows
  }

  get errorMessage(): string | null {
    return this.message === null &&
      this.isNewMember &&
      this.plan !== null &&
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      !this.$refs.memberCategorySelect.isLoadingOptions &&
      this.$refs.memberCategorySelect.options.length === 0
      ? "There are no active Categories for this Plan"
      : this.message
  }

  private companySelected(company: Company | null) {
    this.companyId = company == null ? null : company.id
    this.request.selectedMember.companyId = this.companyId

    if (company == null) {
      this.plan = null
    } else {
      if (!this.isNewMember) {
        this.$refs.memberSelect.model = null
        this.member = null
      }
      this.plan = company.plan
    }
  }

  private async memberSelected(member: Member | null) {
    this.member = member

    if (member != null) {
      this.request.memberId = member.id

      void (await Axios.get(`/api/insuranceCalculation/member?memberId=${member.id}`)
        .then((response: AxiosResponse) => {
          this.message = null
          this.applySelectedMember(response.data)
        })
        .catch((reason: AxiosError<any>) => {
          this.message = reason.response?.data.message
        }))
    }
  }

  private memberCategoriesPath(): string | null {
    return this.companyId == null
      ? null
      : `/api/insuranceCalculation/${this.companyId}/memberCategories`
  }

  private memberCategorySelected(category: MemberCategory | null) {
    this.request.selectedMember.memberCategory = category
  }

  private applySelectedMember(selectedMember: SelectedMember) {
    this.request.selectedMember = selectedMember

    this.$refs.groupSelect.model = _.startCase(selectedMember.occupationGroup!)
    this.groupSelected(_.startCase(selectedMember.occupationGroup!))
    this.$refs.stateSelect.model = selectedMember.state
    this.stateSelected(selectedMember.state!)
    this.$refs.memberCategorySelect.model = selectedMember.memberCategory
    this.memberCategorySelected(selectedMember.memberCategory)

    this.dateOfBirth = format(new Date(this.request.selectedMember.dateOfBirth!), "dd/MM/yyyy")
  }

  private memberOptionSelected() {
    if (this.isNewMember) {
      this.$refs.memberSelect.model = null
      this.member = null
      this.request.memberId = null
    }
  }

  private groupSelected(group: string | null) {
    this.request.selectedMember.occupationGroup = group!.replace(/\s+/g, "")
  }

  private stateSelected(state: string | null) {
    this.request.selectedMember.state = state
  }

  private memberLabel(): (option: Member) => string {
    return (option: Member) => `${option.surname} [${option.login}] - ${option.companyName}`
  }

  private formatDates() {
    this.request.calculationDate = getIsoDate(this.calculationDate)!
    this.request.selectedMember.dateOfBirth = getIsoDate(this.dateOfBirth)!
  }

  private async downloadInsuranceCalculation() {
    if (!this.isInsuranceCalculationDisabled) {
      this.insuranceCalculationInProgress = true
      this.formatDates()
      await restPostDownloadFile(
        `/api/insuranceCalculation`,
        `InsuranceCalculation.pdf`,
        this.request
      )
        .then(() => (this.message = null))
        .catch((reason: AxiosError<any>) => {
          this.message = reason.response?.data.message
        })
        .finally(() => (this.insuranceCalculationInProgress = false))
    }
  }

  private async runInsuranceCalculation() {
    if (!this.isInsuranceCalculationDisabled) {
      this.insuranceCalculationInProgress = true
      this.formatDates()
      await Axios.post(`/api/insuranceCalculation/data`, this.request)
        .then((response: AxiosResponse) => {
          this.message = null
          this.insuranceCalculationData = response.data
        })
        .catch((reason: AxiosError<any>) => {
          this.message = reason.response?.data.message
        })
        .finally(() => (this.insuranceCalculationInProgress = false))
    }
  }

  private async downloadCalculationsExport() {
    const now = format(new Date(), "ddMMyyyy")
    await restDownloadFile(
      `/api/insuranceCalculation/exportXlsx`,
      `InsuranceCalculations - ${now}.xlsx`
    )
      .then(() => (this.message = null))
      .catch((reason: AxiosError<any>) => {
        this.message = reason.response?.data.message
      })
  }
}
